import { Component, EventEmitter, Input, OnInit, Output, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { switchMap } from 'rxjs/operators';
import { FatService, FatDto, FatStartRequestDto } from '../../../../core/services/fat.service';
import { FatTemplateService } from '../../../../core/services/fat-template.service';
import { Project, MetaStatus } from '../../../../core/models/models';
import { ConfirmDialogComponent } from '../../../../shared/components/confirm-dialog.component';
import { TaskNotificationService } from '../../../../core/services/task-notification.service';

@Component({
  selector: 'app-project-fat',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, DatePipe, ConfirmDialogComponent],
  templateUrl: './project-fat.component.html',
  styleUrl: './project-fat.component.scss'
})
export class ProjectFatComponent implements OnInit {
  private fatService = inject(FatService);
  private fatTemplateService = inject(FatTemplateService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private taskNotificationService = inject(TaskNotificationService);

  @Input({ required: true }) projectId!: number;
  @Input() project: Project | null = null;
  @Output() error = new EventEmitter<string>();
  @Output() started = new EventEmitter<void>();

  fats: FatDto[] = [];
  showStartDialog = false;
  isLoading = false;
  isLoadingExport = false;
  hasValidatedTemplate = true;
  showErrorDialog = false;
  errorDialogTitle = '';
  errorDialogMessage = '';

  canStartFat(): boolean {
    return this.project?.metaStatus === MetaStatus.VALIDE;
  }

  hasDraftFat(): boolean {
    return this.fats.some(f => (f.statusMeta === 'DRAFT' || f.status === 'EN_COURS') && f.status !== 'FAT non conforme');
  }

  hasCompletedCompliantLatestFat(): boolean {
    const latestFat = this.getLatestFat();
    if (!latestFat) {
      return false;
    }
    const isCompleted = !!latestFat.finishedAt || latestFat.status === 'FAT conforme';
    return isCompleted && latestFat.status !== 'FAT non conforme';
  }

  private getLatestFat(): FatDto | null {
    if (!this.fats.length) {
      return null;
    }
    return this.fats.reduce((latest, current) => {
      const latestTime = new Date(latest.createdAt).getTime();
      const currentTime = new Date(current.createdAt).getTime();
      return currentTime > latestTime ? current : latest;
    });
  }

  ngOnInit(): void {
    console.log('ProjectFatComponent ngOnInit, projectId:', this.projectId);
    this.fatTemplateService.hasValidatedTemplate().subscribe({
      next: (res) => {
        this.hasValidatedTemplate = res.hasValidated;
        this.cdr.detectChanges();
      },
      error: () => {
        this.hasValidatedTemplate = false;
        this.cdr.detectChanges();
      }
    });
    this.refresh();
  }

  refresh(): void {
    console.log('ProjectFatComponent refresh, projectId:', this.projectId);
    this.isLoading = true;
    this.fatService.getProjectFats(this.projectId).subscribe({
      next: (data) => {
        console.log('ProjectFatComponent refresh success, data:', data);
        this.fats = data;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('ProjectFatComponent refresh error:', err);
        this.error.emit("Erreur lors du chargement des FAT");
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  selectFat(fat: FatDto): void {
    this.router.navigate(['/projects', this.projectId, 'fats', fat.id]);
  }

  exportFat(fat: FatDto, event: Event): void {
    event.stopPropagation();
    this.isLoadingExport = true;
    const taskId = this.taskNotificationService.start('Export FAT', 'Génération du PDF en cours...');
    this.fatService.startExportJob(this.projectId, fat.id).pipe(
      switchMap(job => this.fatService.waitForExportJob(this.projectId, job.jobId)),
      switchMap(job => this.fatService.downloadExportJob(this.projectId, job.jobId))
    ).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `fat-${fat.fatNumber || fat.id}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        this.taskNotificationService.succeed(taskId, 'PDF généré.');
        this.isLoadingExport = false;
      },
      error: (err) => {
        console.error('Erreur lors de l\'export de la FAT', err);
        this.taskNotificationService.fail(taskId, err?.message || 'La génération du PDF a échoué.');
        this.error.emit(err?.message || 'Une erreur est survenue lors de la génération du fichier.');
        this.isLoadingExport = false;
      }
    });
  }

  askStartFat(): void {
    if (!this.hasValidatedTemplate) {
      this.showErrorModal('Template non validé', 'Impossible de démarrer une FAT : aucun template FAT validé n\'existe. Veuillez d\'abord valider un template.');
      return;
    }
    if (this.hasDraftFat()) {
      this.showErrorModal('FAT en cours', 'Impossible de démarrer une nouvelle FAT : une FAT en statut DRAFT existe déjà pour ce projet.');
      return;
    }
    if (this.hasCompletedCompliantLatestFat()) {
      this.showErrorModal('FAT terminée conforme', 'Impossible de démarrer une nouvelle FAT : la dernière FAT du projet est terminée et conforme.');
      return;
    }
    this.confirmStart();
  }

  showErrorModal(title: string, message: string): void {
    this.errorDialogTitle = title;
    this.errorDialogMessage = message;
    this.showErrorDialog = true;
  }

  closeErrorDialog(): void {
    this.showErrorDialog = false;
  }

  cancelStart(): void { this.showStartDialog = false; }

  confirmStart(): void {
    const payload: FatStartRequestDto = {
      firstFat: this.fats.length === 0,
      workflowId: null
    };
    this.fatService.startFat(this.projectId, payload).subscribe({
      next: () => {
        this.showStartDialog = false;
        this.started.emit();
        this.refresh();
      },
      error: (err) => {
        const message = err?.error?.message || 'Erreur lors du démarrage de la FAT';
        this.showErrorModal('Erreur', message);
        this.showStartDialog = false;
      }
    });
  }
}
